import React from 'react';
import { useUser, SignInButton, SignUpButton } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Zap, Trophy, Users, TrendingUp, Award, Sparkles } from 'lucide-react';

const HomePage = () => {
  const { isSignedIn } = useUser();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-secondary via-secondary/95 to-secondary/90 text-surface relative overflow-hidden py-20 md:py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-surface mb-4 leading-tight">
            Welcome to the <span className="text-primary">Future</span> of Student Engagement
          </h1>
          <p className="text-surface/80 text-base sm:text-lg mb-8">
            Join thousands of Nigerian students in the ultimate platform for sharing, competing, and growing together.
            Create content, win contests, and test your knowledge in real-time.
          </p>

          {isSignedIn ? (
            <div className="flex justify-center gap-4 flex-wrap">
              <Link to="/feed" className="bg-primary hover:bg-primary/90 text-secondary px-8 py-4 rounded-lg font-bold text-lg shadow-lg transition transform hover:scale-105">
                Explore Feed
              </Link>
              <Link to="/contests" className="bg-surface hover:bg-surface/90 text-secondary px-8 py-4 rounded-lg font-bold text-lg shadow-lg transition transform hover:scale-105">
                View Contests
              </Link>
            </div>
          ) : (
            <div className="flex justify-center gap-4 flex-wrap">
              <SignUpButton mode="modal">
                <button className="bg-primary hover:bg-primary/90 text-secondary px-8 py-4 rounded-lg font-bold text-lg shadow-lg transition transform hover:scale-105">
                  Get Started Free
                </button>
              </SignUpButton>
              <SignInButton mode="modal">
                <button className="bg-surface hover:bg-surface/90 text-secondary px-8 py-4 rounded-lg font-bold text-lg shadow-lg transition transform hover:scale-105">
                  Sign In
                </button>
              </SignInButton>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-4">Powerful Features</h2>
            <p className="text-text-light text-lg max-w-2xl mx-auto">
              Everything you need to express yourself, compete, and grow with your peers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-surface rounded-2xl p-8 shadow-lg hover:shadow-xl transition border-l-4 border-primary hover:translate-y-[-4px]">
              <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="text-primary" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-secondary mb-3">Social Feed</h3>
              <p className="text-text-light leading-relaxed">
                Share your moments, connect with peers, and build your community. Post photos, videos, and updates in real-time.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-surface rounded-2xl p-8 shadow-lg hover:shadow-xl transition border-l-4 border-accent hover:translate-y-[-4px]">
              <div className="bg-accent/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Trophy className="text-accent" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-secondary mb-3">Contests</h3>
              <p className="text-text-light leading-relaxed">
                Participate in exciting contests with prizes and recognition. Showcase your talents and win big alongside thousands of competitors.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-surface rounded-2xl p-8 shadow-lg hover:shadow-xl transition border-l-4 border-success hover:translate-y-[-4px]">
              <div className="bg-success/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Zap className="text-success" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-secondary mb-3">Live Quiz</h3>
              <p className="text-text-light leading-relaxed">
                Test your knowledge in real-time competitions. Answer quick questions, compete with friends, and climb the leaderboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary text-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-primary mb-2">50K+</div>
              <p className="text-surface/70">Active Users</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-accent mb-2">1000+</div>
              <p className="text-surface/70">Contests Held</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-primary mb-2">₦100M+</div>
              <p className="text-surface/70">Prizes Awarded</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-accent mb-2">15K+</div>
              <p className="text-surface/70">Daily Active Users</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-secondary text-center mb-16">Why Join Uni9ja Hub?</h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              {[
                { icon: Users, title: 'Connect', desc: 'Meet like-minded students across Nigeria' },
                { icon: Award, title: 'Showcase Talents', desc: 'Win recognition and prizes for your skills' },
                { icon: TrendingUp, title: 'Grow Together', desc: 'Learn from peers and improve daily' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10">
                      <item.icon className="text-primary" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-secondary">{item.title}</h3>
                    <p className="text-text-light mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              {[
                { icon: Zap, title: 'Real-Time Action', desc: 'Instant notifications and live updates' },
                { icon: Trophy, title: 'Earn Rewards', desc: 'Accumulate points and unlock badges' },
                { icon: Sparkles, title: 'Safe Community', desc: 'Moderated environment for all users' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-accent/10">
                      <item.icon className="text-accent" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-secondary">{item.title}</h3>
                    <p className="text-text-light mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-secondary text-center mb-4">Creativity Meets Purpose</h2>
        <p className="text-text-light text-center">Uni9ja Media Hub is where young creatives come to be informed, create, and make impact.</p>
        <div className="text-center mt-4">
          <Link to="/about" className="text-primary hover:underline">Learn more →</Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-secondary to-secondary/90 text-surface py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Join the Movement?</h2>
          <p className="text-lg text-surface/80 mb-10">Start sharing, competing, and growing with thousands of students today.</p>
          
          {!isSignedIn && (
            <div className="flex justify-center gap-4 flex-wrap">
              <SignUpButton mode="modal">
                <button className="bg-primary hover:bg-primary/90 text-secondary px-8 py-4 rounded-lg font-bold text-lg shadow-lg transition transform hover:scale-105">
                  Sign Up Now
                </button>
              </SignUpButton>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;